import React, { useMemo, useState } from 'react';
import { MaskIcon } from './Icon';
import './TimePicker.css';

export interface ServiceSlot {
  key: string;
  label: string;
  icon: string;
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
  hours: number[];
}

interface TimePickerProps {
  allHours: number[];
  minuteStep: number;
  selectedHour: number | null;
  selectedMinute: number | null;
  defaultHour?: number;
  defaultMinute?: number;
  maxMinuteByHour?: Record<number, number>;
  services?: ServiceSlot[];
  onHourChange: (h: number) => void;
  onMinuteChange: (m: number) => void;
}

const DEFAULT_SERVICES: ServiceSlot[] = [
  { key: 'midi', label: 'Dejeuner', icon: '☀️', startHour: 12, startMinute: 0, endHour: 13, endMinute: 30, hours: [12, 13] },
  { key: 'soir', label: 'Diner', icon: '🌙', startHour: 19, startMinute: 0, endHour: 21, endMinute: 30, hours: [19, 20, 21] },
];

export function TimePicker({
  allHours, minuteStep, selectedHour, selectedMinute,
  defaultHour, defaultMinute,
  maxMinuteByHour, services = DEFAULT_SERVICES,
  onHourChange, onMinuteChange,
}: TimePickerProps) {
  const [showServices, setShowServices] = useState(false);
  const [serviceFilter, setServiceFilter] = useState<string | null>(null);

  // Heures filtrées par service
  const filteredHours = useMemo(() => {
    if (!serviceFilter) return allHours;
    const svc = services.find((s) => s.key === serviceFilter);
    return svc ? allHours.filter((h) => svc.hours.includes(h)) : allHours;
  }, [allHours, serviceFilter, services]);

  const displayHour = selectedHour ?? defaultHour ?? filteredHours[0];
  const hourActive = selectedHour !== null;

  // Si l'heure affichée n'est plus dans le filtre, recaler
  const effectiveHour = filteredHours.includes(displayHour) ? displayHour : filteredHours[0];

  const maxMin = maxMinuteByHour?.[effectiveHour] ?? 50;
  const minutes = useMemo(() => {
    const arr: number[] = [];
    for (let m = 0; m <= maxMin; m += minuteStep) arr.push(m);
    return arr;
  }, [maxMin, minuteStep]);

  const displayMinute = selectedMinute ?? defaultMinute ?? minutes[0];
  const minute = minutes.includes(displayMinute) ? displayMinute : minutes[0];
  const minActive = selectedMinute !== null;

  const activeServiceLabel = serviceFilter
    ? services.find((s) => s.key === serviceFilter)?.label
    : null;

  const selectService = (svcKey: string | null) => {
    setServiceFilter(svcKey);
    if (svcKey) {
      const svc = services.find((s) => s.key === svcKey);
      if (svc) {
        onHourChange(svc.startHour);
        onMinuteChange(svc.startMinute);
      }
    }
    setShowServices(false);
  };

  return (
    <>
      <div className="tp-row">
        <button
          className={`date-chip agenda-chip ${serviceFilter ? 'filtered' : ''}`}
          onClick={() => setShowServices(true)}
        >
          <MaskIcon name="clock" size={28} color="var(--color-primary)" />
          {activeServiceLabel && <span className="tp-filter-dot" />}
        </button>

        <button
          className={`date-chip tp-chip ${hourActive ? 'active' : ''}`}
          onClick={() => {
            const el = document.getElementById('tp-hour-select');
            el?.focus(); el?.click();
          }}
        >
          <span className="date-dow">▲</span>
          <span className="date-num">{effectiveHour}</span>
          <span className="date-month">▼</span>
          <select
            id="tp-hour-select"
            className="tp-hidden-select"
            value={effectiveHour}
            onChange={(e) => onHourChange(Number(e.target.value))}
          >
            {filteredHours.map((h) => (
              <option key={h} value={h}>{h}h</option>
            ))}
          </select>
        </button>

        <span className="tp-sep">h</span>

        <button
          className={`date-chip tp-chip ${minActive ? 'active' : ''}`}
          onClick={() => {
            const el = document.getElementById('tp-min-select');
            el?.focus(); el?.click();
          }}
        >
          <span className="date-dow">▲</span>
          <span className="date-num">{String(minute).padStart(2, '0')}</span>
          <span className="date-month">▼</span>
          <select
            id="tp-min-select"
            className="tp-hidden-select"
            value={minute}
            onChange={(e) => onMinuteChange(Number(e.target.value))}
          >
            {minutes.map((m) => (
              <option key={m} value={m}>{String(m).padStart(2, '0')} min</option>
            ))}
          </select>
        </button>

        {/* Info service à droite */}
        {(() => {
          const svc = services.find((s) => s.hours.includes(effectiveHour));
          if (!svc) return null;
          return (
            <div className="tp-info">
              <span className="tp-info-label">{svc.icon} {svc.label}</span>
              <span className="tp-info-range">{svc.startHour}h{String(svc.startMinute).padStart(2, '0')} – {svc.endHour}h{String(svc.endMinute).padStart(2, '0')}</span>
            </div>
          );
        })()}
      </div>

      {/* Service picker popup */}
      {showServices && (
        <div className="tp-overlay" onClick={() => setShowServices(false)}>
          <div className="tp-sheet" onClick={(e) => e.stopPropagation()}>
            <h3 className="tp-sheet-title">Choisir un service</h3>

            {/* Option Tous */}
            <button
              className={`tp-service-row ${!serviceFilter ? 'selected' : ''}`}
              onClick={() => selectService(null)}
            >
              <span className="tp-service-icon">🕐</span>
              <div className="tp-service-info">
                <strong>Tous les creneaux</strong>
                <span>Afficher toutes les heures</span>
              </div>
            </button>

            {services.map((svc) => (
              <button
                key={svc.key}
                className={`tp-service-row ${serviceFilter === svc.key ? 'selected' : ''}`}
                onClick={() => selectService(svc.key)}
              >
                <span className="tp-service-icon">{svc.icon}</span>
                <div className="tp-service-info">
                  <strong>{svc.label}</strong>
                  <span>{svc.startHour}h{String(svc.startMinute).padStart(2, '0')} – {svc.endHour}h{String(svc.endMinute).padStart(2, '0')}</span>
                </div>
              </button>
            ))}

            <button className="tp-sheet-close" onClick={() => setShowServices(false)}>Fermer</button>
          </div>
        </div>
      )}
    </>
  );
}
